import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SEO } from '../seo/SEO';

/**
 * Politique de confidentialité — double conformité :
 *  - Loi marocaine 09-08 (CNDP) : déclaration obligatoire dès collecte de
 *    données personnelles (email, téléphone, etc.) sur un site marocain
 *  - RGPD (UE) pour les clientes EU : droits d'accès, rectification,
 *    effacement, portabilité, opposition (articles 13-22)
 *
 * Les sous-traitants listés ci-dessous correspondent au stack actuel :
 * Hetzner (hosting), Resend (email transactionnel), Bugsnag/SmartBear
 * (supervision technique), Porkbun (DNS), CNDP (déclaration). À mettre à
 * jour si la stack évolue.
 *
 * Tout tiers qui reçoit des données de visiteurs DOIT figurer en section 6,
 * y compris les outils techniques : Bugsnag est chargé sur chaque page par
 * `src/lib/bugsnag.ts` et reçoit IP + user-agent + URL, ce qui en fait un
 * sous-traitant au même titre que l'hébergeur. Il était absent de cette
 * liste alors qu'il collectait déjà.
 */
export function PrivacyPolicyPage() {
  const { t } = useTranslation();

  return (
    <section className="max-w-editorial mx-auto px-6 sm:px-10 py-20">
      <SEO titleKey="seo.privacy.title" descriptionKey="seo.privacy.description" />
      <div className="grid grid-cols-12 gap-8 mb-16 border-b border-linen pb-12">
        <div className="col-span-12 md:col-span-3">
          <span className="editorial-number text-5xl block">N°RGPD</span>
          <div className="editorial-label mt-3">— Confidentialité</div>
        </div>
        <div className="col-span-12 md:col-span-9">
          <h1 className="font-display text-[clamp(2.5rem,5vw,4.5rem)] leading-[0.95]">
            Politique de confidentialité
          </h1>
          <p className="mt-6 editorial-label">Dernière mise à jour : mai 2026</p>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-8">
        <div className="col-span-12 md:col-span-9 md:col-start-4 space-y-12 font-body text-base text-ink-soft leading-relaxed">

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">1. Préambule</h2>
            <p>
              L'atelier <strong>Fati Caftan</strong>, édité par <strong>Fatima [Nom de famille à compléter]</strong>{' '}
              (ci-après « la Responsable de traitement »), accorde une importance fondamentale
              à la protection de la vie privée et des données personnelles des visiteurs et
              clientes du site faticaftan.com.
            </p>
            <p className="mt-3">
              La présente politique a pour objet d'informer de manière claire et transparente
              sur les données collectées, leurs finalités, leurs destinataires, leur durée de
              conservation et les droits dont vous disposez. Elle est conforme à la loi
              marocaine n°09-08 relative à la protection des personnes physiques à l'égard du
              traitement des données à caractère personnel, ainsi qu'au Règlement (UE)
              2016/679 (RGPD) pour les visiteurs résidant dans l'Union européenne.
            </p>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">
              2. Responsable de traitement
            </h2>
            <ul className="space-y-1 list-disc list-inside">
              <li><strong>Nom :</strong> Fatima [Nom de famille à compléter]</li>
              <li><strong>Statut :</strong> Particulière — atelier artisanal individuel</li>
              <li><strong>Adresse :</strong> [À compléter — ville, Maroc]</li>
              <li><strong>Email :</strong> fatima@faticaftan.com</li>
              <li><strong>Téléphone :</strong> +212 7 01 04 86 61</li>
            </ul>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">3. Données collectées</h2>
            <p>Le site collecte uniquement les données nécessaires aux services proposés :</p>

            <h3 className="font-display text-xl italic text-ink mt-4 mb-2">
              3.1 Lors d'une prise de rendez-vous (sur mesure)
            </h3>
            <ul className="space-y-1 list-disc list-inside">
              <li>Nom et prénom</li>
              <li>Adresse email</li>
              <li>Numéro de téléphone</li>
              <li>Date et créneau de rendez-vous souhaités</li>
              <li>Pièce de référence (caftan, takchita, etc.)</li>
              <li>Notes complémentaires (facultatif)</li>
            </ul>

            <h3 className="font-display text-xl italic text-ink mt-4 mb-2">
              3.2 Lors d'une réservation de pièce prêt à porter
            </h3>
            <p>
              Aucune donnée n'est collectée par le site lors de la réservation d'une pièce
              prête à porter. Le bouton <em>« Réserver via WhatsApp »</em> ouvre simplement
              une conversation pré-remplie (référence et taille du produit) côté application
              WhatsApp. L'échange ultérieur (coordonnées, mode de paiement, mode de remise) se
              déroule entièrement dans cette conversation tierce, soumise aux conditions
              d'utilisation et à la politique de confidentialité de WhatsApp (Meta Platforms).
            </p>
            <p className="mt-3 text-sm italic">
              Aucune donnée bancaire n'est saisie ni traitée par le site. Le paiement
              s'effectue en présentiel ou par virement, selon les modalités convenues
              directement avec l'atelier.
            </p>

            <h3 className="font-display text-xl italic text-ink mt-4 mb-2">
              3.3 Données techniques (automatiques)
            </h3>
            <ul className="space-y-1 list-disc list-inside">
              <li>
                <strong>Adresse IP</strong> — utilisée pour la prévention des abus (limitation
                de débit sur les formulaires de connexion et de prise de rendez-vous, détection
                d'envois automatisés), la supervision technique du site et la mesure d'audience
                agrégée (nombre de visites, pays, pages consultées). Elle n'est jamais associée
                à votre identité, ni utilisée à des fins publicitaires, ni revendue. Non stockée
                durablement : voir la durée de conservation en section 7.
              </li>
              <li>
                <strong>Données de navigation</strong> stockées localement dans votre
                navigateur :
                <ul className="list-disc list-inside ms-6 mt-1">
                  <li><code>handcrafted.auth</code> — jeton de session (accès admin uniquement)</li>
                  <li><code>handcrafted.cart</code> — contenu du panier d'achat</li>
                  <li><code>handcrafted.theme</code> — thème visuel sélectionné</li>
                  <li><code>handcrafted.preview-key</code> — clé de prévisualisation admin</li>
                  <li><code>i18nextLng</code> — langue préférée (FR/EN/AR)</li>
                </ul>
                Ces éléments restent dans votre navigateur et ne sont jamais transmis à des
                tiers. Vous pouvez les supprimer à tout moment via les paramètres de votre
                navigateur.
              </li>
              <li>
                <strong>Aucun cookie publicitaire ni de traçage comportemental</strong> n'est
                utilisé. Le site n'intègre ni Google Analytics, ni pixel Meta, ni régie
                publicitaire, et ne suit pas votre navigation d'un site à l'autre.
              </li>
              <li>
                <strong>Supervision technique</strong> — un outil de détection de pannes
                (Bugsnag) est chargé sur chaque page. Il enregistre les erreurs JavaScript
                rencontrées et compte les sessions, afin que l'atelier soit averti si le site
                cesse de fonctionner. Il reçoit à ce titre votre adresse IP, votre navigateur et
                l'URL consultée. Il ne dépose aucun cookie, ne sert à aucun ciblage publicitaire
                et ne permet pas de vous identifier.
              </li>
            </ul>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">
              4. Finalités du traitement
            </h2>
            <p>Les données collectées sont utilisées exclusivement pour :</p>
            <ul className="mt-3 space-y-1 list-disc list-inside">
              <li>Confirmer et organiser les rendez-vous de mesures</li>
              <li>Préparer et expédier les commandes prêt à porter</li>
              <li>Envoyer les emails de confirmation et notifications liées</li>
              <li>Répondre aux demandes et réclamations</li>
              <li>Respecter les obligations légales et fiscales (facturation, comptabilité)</li>
              <li>Prévenir la fraude et les abus (limitation de débit, anti-bot)</li>
            </ul>
            <p className="mt-3">
              <strong>Aucune donnée n'est utilisée à des fins commerciales secondaires</strong>{' '}
              (revente, prospection tierce, profilage). Aucun emailing marketing n'est envoyé
              sans consentement préalable explicite.
            </p>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">5. Base légale</h2>
            <p>Les traitements reposent, selon le cas, sur :</p>
            <ul className="mt-3 space-y-1 list-disc list-inside">
              <li>
                <strong>L'exécution d'un contrat ou de mesures précontractuelles</strong>{' '}
                (RGPD art. 6.1.b) — commande, rendez-vous, livraison.
              </li>
              <li>
                <strong>Le respect d'une obligation légale</strong> (RGPD art. 6.1.c) —
                facturation, archivage comptable.
              </li>
              <li>
                <strong>L'intérêt légitime du Responsable</strong> (RGPD art. 6.1.f) —
                prévention de la fraude, sécurité du site.
              </li>
              <li>
                <strong>Votre consentement</strong> (RGPD art. 6.1.a) — pour toute future
                inscription à une newsletter.
              </li>
            </ul>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">
              6. Destinataires et sous-traitants
            </h2>
            <p>
              Vos données sont accessibles à la propriétaire de l'atelier et, le cas échéant,
              à du personnel habilité. Elles sont également confiées à des prestataires
              techniques agissant en qualité de sous-traitants au sens du RGPD (art. 28) :
            </p>
            <ul className="mt-3 space-y-2 list-disc list-inside">
              <li>
                <strong>Hetzner Online GmbH</strong> (Allemagne, serveurs à Helsinki, Finlande)
                — hébergement du site et de la base de données. Sous-traitant certifié ISO
                27001, données conservées en Union européenne.
              </li>
              <li>
                <strong>Resend, Inc.</strong> (États-Unis, traitement région UE eu-west-1) —
                envoi des emails transactionnels (confirmations de rendez-vous et de commandes).
                Transfert encadré par les Clauses Contractuelles Types (CCT) de la Commission
                européenne.
              </li>
              <li>
                <strong>Bugsnag — SmartBear Software, Inc.</strong> (États-Unis) — supervision
                technique du site : collecte des erreurs JavaScript et comptage des sessions
                pour détecter les pannes. Reçoit l'adresse IP, le navigateur et l'URL consultée
                lors de chaque chargement de page. Transfert hors UE encadré par l'accord de
                sous-traitance (DPA) de l'éditeur et les Clauses Contractuelles Types de la
                Commission européenne.
              </li>
              <li>
                <strong>Porkbun LLC</strong> (États-Unis) — gestion du nom de domaine (DNS
                uniquement, aucune donnée personnelle utilisateur transmise).
              </li>
              <li>
                <strong>Meta Platforms / WhatsApp</strong> (États-Unis) — uniquement lorsque vous
                cliquez volontairement sur un lien WhatsApp depuis le site ; à ce moment, votre
                échange se déroule dans l'écosystème WhatsApp soumis à ses propres conditions.
              </li>
            </ul>
            <p className="mt-3">
              Aucun transfert hors UE ne s'effectue sans encadrement juridique conforme (CCT,
              décision d'adéquation, ou consentement explicite).
            </p>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">
              7. Durée de conservation
            </h2>
            <ul className="space-y-2 list-disc list-inside">
              <li><strong>Rendez-vous et commandes :</strong> 5 ans à compter de la fin de la relation contractuelle (obligation comptable et fiscale marocaine).</li>
              <li><strong>Données de facturation :</strong> 10 ans (obligation comptable, Code Général des Impôts).</li>
              <li><strong>Comptes administrateurs :</strong> tant que le compte est actif, supprimés sur demande ou cessation d'activité.</li>
              <li><strong>Logs techniques et IP rate-limit :</strong> 30 jours maximum, puis suppression automatique.</li>
              <li><strong>Rapports d'audience :</strong> conservés sans limite de durée, car uniquement agrégés (compteurs par page, par pays, par source). Ils ne contiennent aucune adresse IP ni aucune donnée permettant de vous identifier.</li>
              <li><strong>Erreurs et sessions techniques (Bugsnag) :</strong> conservées selon la politique de rétention de l'éditeur, puis supprimées automatiquement.</li>
              <li><strong>Données stockées dans votre navigateur (localStorage) :</strong> jusqu'à effacement par vous-même ou expiration du jeton (24 h pour l'authentification).</li>
            </ul>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">
              8. Vos droits
            </h2>
            <p>Conformément à la loi 09-08 et au RGPD, vous disposez des droits suivants :</p>
            <ul className="mt-3 space-y-1 list-disc list-inside">
              <li><strong>Droit d'accès</strong> — obtenir copie des données vous concernant.</li>
              <li><strong>Droit de rectification</strong> — corriger des données inexactes.</li>
              <li><strong>Droit d'effacement (« droit à l'oubli »)</strong> — supprimer vos données sous conditions.</li>
              <li><strong>Droit à la limitation</strong> — figer le traitement le temps d'une vérification.</li>
              <li><strong>Droit d'opposition</strong> — vous opposer pour motif légitime.</li>
              <li><strong>Droit à la portabilité</strong> — récupérer vos données dans un format structuré et lisible.</li>
              <li><strong>Droit de retirer votre consentement</strong> à tout moment, sans effet rétroactif.</li>
              <li><strong>Droit d'introduire une réclamation</strong> auprès de l'autorité de contrôle compétente (CNDP au Maroc, CNIL en France, autres autorités UE selon votre pays).</li>
            </ul>
            <p className="mt-3">
              Pour exercer ces droits, écrivez-nous à <strong>fatima@faticaftan.com</strong> en
              précisant l'objet de votre demande. Une réponse vous sera apportée dans un délai
              maximal de 30 jours. Toute demande devra être accompagnée d'une copie d'une pièce
              d'identité afin de vérifier votre identité.
            </p>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">9. Sécurité</h2>
            <p>Les mesures techniques et organisationnelles mises en œuvre incluent :</p>
            <ul className="mt-3 space-y-1 list-disc list-inside">
              <li>Connexion intégralement chiffrée en HTTPS (TLS 1.2+) avec HSTS preload.</li>
              <li>Mots de passe administrateurs hachés avec BCrypt (jamais stockés en clair).</li>
              <li>Jetons d'authentification JWT signés HS256, secret ≥ 32 octets.</li>
              <li>Limitation de débit sur les formulaires sensibles (login, prise de rendez-vous).</li>
              <li>Détection anti-bot par champ honeypot + délai minimum de soumission.</li>
              <li>Validation par magic-bytes des images uploadées.</li>
              <li>Validation stricte des URL d'images (rejet des schémas <code>data:</code>, <code>javascript:</code>, etc.).</li>
              <li>Sauvegardes quotidiennes chiffrées de la base de données.</li>
              <li>En-têtes de sécurité HTTP : CSP, X-Frame-Options, X-Content-Type-Options, Referrer-Policy, Permissions-Policy.</li>
              <li>Accès SSH au serveur restreint par clés publiques, port 22 filtré par UFW.</li>
            </ul>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">10. Cookies et stockage local</h2>
            <p>
              Le site n'utilise <strong>aucun cookie</strong> à des fins publicitaires ou de
              traçage. Le stockage local du navigateur (localStorage) est utilisé uniquement
              pour le fonctionnement du site (préférence de langue, panier d'achat, session
              admin, thème visuel), tel que décrit en section 3.3.
            </p>
            <p className="mt-3">
              Pour effacer ces données, utilisez la fonction « Effacer les données de
              navigation » ou « Effacer les cookies et autres données » de votre navigateur,
              en restreignant le périmètre à faticaftan.com.
            </p>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">
              11. Mineurs
            </h2>
            <p>
              Le site n'est pas destiné aux mineurs de moins de 16 ans. Aucune donnée n'est
              sciemment collectée auprès d'enfants. Si vous êtes parent ou tuteur et constatez
              qu'un mineur nous a transmis des données, contactez-nous pour suppression
              immédiate.
            </p>
          </article>

          <article>
            <h2 className="font-display text-3xl italic text-ink mb-4">
              12. Modifications
            </h2>
            <p>
              La présente politique peut être modifiée pour refléter une évolution du site, de
              la législation ou des prestataires. La date en tête de page indique la dernière
              version. Vous êtes invités à consulter régulièrement cette page.
            </p>
          </article>

          <p className="pt-4">
            <Link to="/" className="brass-link editorial-label">
              ← {t('productDetail.back')}
            </Link>
          </p>

        </div>
      </div>
    </section>
  );
}
